import { useCallback, useEffect, useState } from 'react';
import { differenceInDays, format } from 'date-fns';
import {
  DeleteButton,
  DownloadButton,
  StepContent,
  Table,
  TextM,
  TTableBodyContent,
  Upload,
} from 'app/components';
import {
  StepContentInputArea,
  StylesSuccessMessage,
  TableBodyFile,
  TableFilesWrapper,
  TableHeadFile,
  UploadArea,
} from './styles';
import { TTableHeadCell } from 'app/components/Table';
import {
  BusinessDocumentTypeLabel,
  DOCUMENT_TYPE,
} from 'app/helpers/documentTypes';
import { iconByFileType } from 'modules/customer/components/DocumentsModal/DocumentsModal';
import { useCustomerContext } from 'modules/customer/context';
import { EFunctions } from 'modules/customer/context/CustomerProvider/customer.interfaces';
import {
  RequiredArea,
  RequiredAreaIcon,
  RequiredDocumentsList,
  RequiredDocumentsListItem,
} from 'modules/customer/components';
import { AttentionRequiredDocuments, ChevronIcon } from 'app/components/Icons';
import { useUploadBusinessFiles } from 'modules/customer/hooks';

type BusinessDocumentTypeLabelType = typeof BusinessDocumentTypeLabel;

const tableBodyFileStyle = {
  display: 'flex',
};

const tableHeadRaw = {
  id: '1',
  content: ['Nome', 'Tipo', 'Data de envio', 'Baixar', 'Excluir'],
};

type TUploadDocumentsProps = {
  personId?: string;
  hasSuccessMessage?: boolean;
};

const UploadBusinessDocument = ({
  personId,
  hasSuccessMessage,
}: TUploadDocumentsProps) => {
  const {
    state: { func, product },
  } = useCustomerContext();
  const {
    handleUploadFiles,
    downloadBusinessFile,
    handleDeleteBusinessFile,
    uploadedFiles,
    uploadedFilesLoading,
  } = useUploadBusinessFiles();

  const [tableHeadContent, setTableHeadContent] = useState<
    TTableHeadCell[] | null
  >(null);
  const [tableBodyContent, setTableBodyContent] = useState<
    TTableBodyContent[] | null
  >(null);
  const [requiredDocuments, updateRequiredDocuments] = useState<
    DOCUMENT_TYPE[]
  >([]);

  const handleDrop = useCallback((acceptedFiles: File[]) => {
    handleUploadFiles(acceptedFiles, personId || '');
  }, []);

  useEffect(() => {
    const businessDocuments =
      func.value === EFunctions.BORROWER
        ? product.variables_configuration?.borrower.business_document
        : product.variables_configuration?.employer_document;

    if (businessDocuments)
      updateRequiredDocuments(
        businessDocuments
          ?.filter((document) => {
            if (!document.required) return false;
            const hasUploaded = uploadedFiles.find(
              (uploadedFile) => uploadedFile.type === document.type,
            );
            return !hasUploaded;
          })
          .map((document) => document.type),
      );
  }, [product, func, uploadedFiles]);

  const renderCreationDate = (createdAt: string) => {
    const date = new Date(createdAt);
    const diffInDays = differenceInDays(new Date(), date);
    return (
      <TextM weight="Regular">
        {diffInDays > 2
          ? format(date, 'dd/MM/yyyy')
          : diffInDays === 0
          ? 'Hoje'
          : `${diffInDays} dia(s) atrás`}
      </TextM>
    );
  };

  useEffect(() => {
    const parsedHeadContent = tableHeadRaw.content.map((item, index) => {
      if (index === 0) {
        return {
          id: String(index),
          content: (
            <TableHeadFile>
              <TextM weight="Bold">Arquivo</TextM>
              <ChevronIcon />
            </TableHeadFile>
          ),
        };
      } else {
        return {
          id: String(index),
          content: <TextM weight="Bold">{item}</TextM>,
        };
      }
    });

    setTableHeadContent(parsedHeadContent);
  }, []);

  useEffect(() => {
    const parsedBodyContent = uploadedFiles.map((item, index) => {
      return {
        id: String(index),
        cells: [
          {
            id: '1',
            content: (
              <TableBodyFile style={tableBodyFileStyle}>
                <div>
                  {
                    iconByFileType[
                      item?.file_extension?.toLocaleLowerCase() ?? 'pdf'
                    ]
                  }
                </div>
                <TextM weight="Semibold">{item?.file_name}</TextM>
              </TableBodyFile>
            ),
          },
          {
            id: '2',
            content: (
              <TextM weight="Regular">
                {
                  BusinessDocumentTypeLabel[
                    (item?.type ||
                      'OTHER') as keyof BusinessDocumentTypeLabelType
                  ]
                }
              </TextM>
            ),
          },
          {
            id: '3',
            content: renderCreationDate(
              item?.created_at || new Date().toISOString(),
            ),
          },
          {
            id: '4',
            content: (
              <DownloadButton
                handleClick={() =>
                  downloadBusinessFile(personId || '', item.id || '', item)
                }
              />
            ),
          },
          {
            id: '5',
            content: (
              <DeleteButton
                handleClick={() => {
                  handleDeleteBusinessFile(item.id || '', personId || '');
                }}
              />
            ),
          },
        ],
      };
    });

    setTableBodyContent(parsedBodyContent);
  }, [uploadedFiles]);

  return (
    <StepContent title="Documentos">
      {hasSuccessMessage && (
        <StylesSuccessMessage>
          <p>
            <b>Cadastro efetuado com sucesso!</b>
          </p>
          <p>
            Caso queira adicionar um documento use o campo abaixo ou clique em
            salvar para sair.
          </p>
        </StylesSuccessMessage>
      )}
      <StepContentInputArea style={{ marginTop: '32px' }}>
        <TableFilesWrapper>
          <Table
            tableHeadCell={tableHeadContent}
            tableBodyContent={tableBodyContent}
          />
        </TableFilesWrapper>
        <UploadArea>
          <Upload handleDrop={handleDrop} loading={uploadedFilesLoading} />
        </UploadArea>
        {requiredDocuments.length > 0 && (
          <RequiredArea>
            <RequiredAreaIcon>
              <AttentionRequiredDocuments />
            </RequiredAreaIcon>
            <TextM weight="Bold">{`${requiredDocuments.length} documentos obrigatórios não foram enviados.`}</TextM>
            <RequiredDocumentsList>
              {requiredDocuments.map((document) => {
                return (
                  <RequiredDocumentsListItem key={document as string}>
                    {
                      BusinessDocumentTypeLabel[
                        document as keyof BusinessDocumentTypeLabelType
                      ]
                    }
                  </RequiredDocumentsListItem>
                );
              })}
            </RequiredDocumentsList>
          </RequiredArea>
        )}
      </StepContentInputArea>
    </StepContent>
  );
};

export default UploadBusinessDocument;
